import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_PRICING_PLAN_PERIOD_ADJUSTMENTS_RESOURCE } from 'src/features/pricingPlan/api/adjustments/useListPricingPlanPeriodAdjustments';
import { useUpdatePricingPlanAdjustment } from 'src/features/pricingPlan/api/adjustments/useUpdatePricingPlanAdjustment';
import AdjustmentFormModal from 'src/features/pricingPlan/components/form/adjustments/AdjustmentFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanPeriodAdjustment } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdatePricingPlanAdjustmentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAdjustment: PricingPlanPeriodAdjustment;
    children?: React.ReactNode;
}

function UpdatePricingPlanAdjustmentFormModal<TData>({
    className,
    semantics,
    children,
    selectedAdjustment,
}: UpdatePricingPlanAdjustmentFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdatePricingPlanAdjustment({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_PERIOD_ADJUSTMENTS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.updateAdjustmentSuccess),
                description: formatMessage(pricingPlanMessages.updateAdjustmentSuccessDescription),
            });
        },
    });

    return (
        <AdjustmentFormModal
            selectedAdjustment={selectedAdjustment}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ planId, adjustmentId: selectedAdjustment.id, parkingId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editAdjustmentSubtitle)}
        >
            {children}
        </AdjustmentFormModal>
    );
}

export default UpdatePricingPlanAdjustmentFormModal;
